<template>
  <div class="d-flex align-items-center">
    <div :class="$style.inputGroup">
      <small :class="$style.inputGroupLabel">{{ $t('placeholder.day') }}</small>
      <input
        list="abstracted-day-input-day-list"

        :class="$style.input"
        :value="model.day"
        name="day"
        placeholder="dd"
        type="tel"
        autocomplete="bday-day"
        :required="isFilledPartially"
        pattern="[0-9]{1,2}"
        maxlength="2"

        @focus="focus += 1"
        @blur="focus -= 1"
        @input="handleInput"
      >
    </div>
    <span
      :class="$style.inputSeparator"
      class="px-4"
    />
    <div :class="$style.inputGroup">
      <small :class="$style.inputGroupLabel">{{ $t('placeholder.month') }}</small>
      <input
        :class="$style.input"
        :value="model.month"
        name="month"
        placeholder="mm"
        type="tel"
        autocomplete="bday-month"
        :required="isFilledPartially"
        pattern="[0-9]{1,2}"
        maxlength="2"

        @focus="focus += 1"
        @blur="focus -= 1"
        @input="handleInput"
      >
    </div>
    <span
      :class="$style.inputSeparator"
      class="px-4"
    />
    <div :class="$style.inputGroup">
      <small :class="$style.inputGroupLabel">{{ $t('placeholder.year') }}</small>
      <input
        :class="$style.input"
        :value="model.year"
        name="year"
        placeholder="yyyy"
        type="tel"
        autocomplete="bday-year"
        :required="isFilledPartially"
        pattern="[1-9][0-9]{3}"
        maxlength="4"

        @focus="focus += 1"
        @blur="focus -= 1"
        @input="handleInput"
      >
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

interface DateModel {
  year: string | null;
  month: string | null;
  day: string | null;
}

const parseNumberOtherwiseNull = (value: string | number): number | null => {
  if (typeof value === 'number') return value;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? null : parsed;
};

const createDateString = ({ year, month, day }: { year: number; month: number; day: number }): string => [
  `${year}`.padStart(4, '0'),
  `${month}`.padStart(2, '0'),
  `${day}`.padStart(2, '0'),
].join('-');

export default Vue.extend({
  i18n: {
    en: {
      placeholder: {
        year: 'year',
        month: 'month',
        day: 'day',
      },
    },
    id: {
      placeholder: {
        year: 'tahun',
        month: 'bulan',
        day: 'hari',
      },
    },
    zh: {
      placeholder: {
        year: '年',
        month: '月',
        day: '日',
      },
    },
  },

  props: {
    value: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      focus: 0,
      model: {
        year: null,
        month: null,
        day: null,
      } as DateModel,
    };
  },

  computed: {
    /**
     * Input is considered filled partially when
     * some of the field is filled. But not all.
     *
     * Note the usage of falsey comparison. Empty string and 0 are considered not filled
     */
    isFilledPartially(): boolean {
      const { year, month, day } = this.model;
      const hasFilledAtLeastOneInput = year || month || day;
      const hasFilledAllInput = year && month && day;

      if (hasFilledAtLeastOneInput) {
        return this.focus > 0 || !hasFilledAllInput;
      } else {
        return false;
      }
    },
  },

  watch: {
    value: {
      handler(value: string | null): void {
        // Prevent modification to current input if user is still filling

        if (this.isFilledPartially) return;
        const { year, month, day } = this.parseStringToDateModel(value);

        this.model.year = year;
        this.model.month = month;
        this.model.day = day;
      },
      immediate: true,
    },

    model: {
      handler(model: DateModel): void {
        const parsed = {
          year: parseNumberOtherwiseNull(model.year || 0),
          month: parseNumberOtherwiseNull(model.month || 0),
          day: parseNumberOtherwiseNull(model.day || 0),
        };

        if (!(parsed.year && parsed.month && parsed.day)) {
          this.$emit('input', null);
          return;
        }

        const createdDate = new Date(
          parsed.year,
          parsed.month - 1,
          parsed.day,
        );
        if (isNaN(createdDate.valueOf())) {
          this.$emit('input', null);
          return;
        }

        const newValue = createDateString({
          year: parsed.year,
          month: parsed.month,
          day: parsed.day,
        });
        this.$emit('input', newValue);
      },
    },
  },

  methods: {
    handleInput(event: Event): void {
      const target = event.target as HTMLInputElement;
      const updateName = target.name as keyof DateModel;

      // Remove non numeric character
      const value = target.value.replace(/[^0-9]/g, '');

      this.model = {
        ...this.model,
        [updateName]: value,
      };
    },

    parseStringToDateModel(value: string | null): DateModel {
      if (value == null || value === '' || value === 'null') {
        return {
          year: null,
          month: null,
          day: null,
        };
      }

      const [year, month, day] = value.split('-');
      return {
        year,
        month,
        day,
      };
    },
  },
});
</script>

<style module>
.inputGroupLabel {
  text-transform: capitalize;
}

.inputSeparator {
  margin-top: 1.5em;
}

.inputGroup input {
  width: 100%;
  display: block;
}
</style>
